import { ChevronLeftIcon } from "@heroicons/react/20/solid";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMatches } from "react-router";
import { HOME_PATH } from "../../shared/routing/appPaths";
import { useWorkspace } from "../workspace";
import { detourOfMatches, detourReturnOfMatches } from "./detourRoutes";

/** Where Escape belongs to something else: typing, an open menu or dialog. */
const ESCAPE_OWNERS = 'input, textarea, select, [contenteditable="true"], [role="menu"], [role="dialog"], [role="listbox"]';

/**
 * The one back control of a detour page (F87), top left above it, the same on every one: it
 * returns to the exact previous place, one step, as the browser's Back does, and so does Escape
 * in the tab worked in. All three take the workspace's one step back (`Workspace.back`), so a
 * nested detour (Connectors, then pairing) returns to Connectors by each (TR-A1-03). A detour
 * opened first, with nothing of DMW before it, goes to its route's `returnTo` or the start page.
 */
export default function DetourBack() {
  const { t } = useTranslation();
  const matches = useMatches();
  const detour = detourOfMatches(matches) !== null;
  const returnTo = detourReturnOfMatches(matches) ?? HOME_PATH;
  const workspace = useWorkspace();
  const { isActive } = workspace;

  const back = useCallback(() => { workspace.back(returnTo); }, [workspace, returnTo]);

  useEffect(() => {
    if (!detour || !isActive) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.repeat) return;
      if (event.target instanceof Element && event.target.closest(ESCAPE_OWNERS) !== null) return;
      if (document.querySelector('[aria-modal="true"]') !== null) return;
      // Decided once every listener had the key: a page that used Escape, such as the review
      // closing its help, marks it, whatever order the listeners were added in.
      window.setTimeout(() => {
        if (!event.defaultPrevented) back();
      }, 0);
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); };
  }, [detour, isActive, back]);

  if (!detour) return null;
  return (
    <div className="pt-3">
      <button type="button" className="app-action-link-muted -ml-1 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-sm" title={t("shell.backKey")} aria-keyshortcuts="Escape" onClick={back}>
        <ChevronLeftIcon className="h-4 w-4" aria-hidden="true" />
        {t("shell.back")}
      </button>
    </div>
  );
}
